/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Client-Side Zero-FFmpeg Audio Render & Export Modal (Task 10.5.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  X,
  Download,
  Sparkles,
  FileAudio,
  CheckCircle2,
  HardDrive,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useAudioMixerStore,
  ExportAudioFormat,
} from '../../stores/useAudioMixerStore';

interface MixerExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveToGallery?: (fileUrl: string) => void;
}

export const MixerExportModal: React.FC<MixerExportModalProps> = ({
  isOpen,
  onClose,
  onSaveToGallery,
}) => {
  const { t } = useTranslation();
  const {
    projectTitle,
    totalDuration,
    tracks,
    duckingSettings,
    isExporting,
    exportProgress,
    exportedAudioUrl,
    startExport,
    resetExport,
  } = useAudioMixerStore();

  const [format, setFormat] = useState<ExportAudioFormat>('wav');
  const [sampleRate, setSampleRate] = useState<number>(48000);
  const [isSavedToGallery, setIsSavedToGallery] = useState(false);

  if (!isOpen) return null;

  const handleStartRendering = async () => {
    setIsSavedToGallery(false);
    await startExport(format, sampleRate);
  };

  const handleDownload = () => {
    if (!exportedAudioUrl) return;
    const link = document.createElement('a');
    link.href = exportedAudioUrl;
    link.download = `${projectTitle.toLowerCase().replace(/[^a-z0-9]/g, '_')}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveToGallery = () => {
    if (!exportedAudioUrl) return;
    onSaveToGallery?.(exportedAudioUrl);
    setIsSavedToGallery(true);
  };

  const handleClose = () => {
    if (isExporting) return;
    resetExport();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-lg bg-surface border border-surface-border rounded-2xl shadow-2xl overflow-hidden flex flex-col space-y-4 p-5">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-surface-border">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary border border-primary/20">
              <FileAudio className="size-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">
                {t('mixer.exportModalTitle')}
              </h3>
              <p className="text-xs text-foreground-muted">
                {t('mixer.exportModalSubtitle')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isExporting}
            className="p-1 rounded-lg text-foreground-muted hover:text-foreground disabled:opacity-40"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Project Summary Strip */}
        <div className="p-3.5 rounded-xl bg-surface-elevated border border-surface-border grid grid-cols-3 gap-2 text-center text-xs">
          <div>
            <span className="text-[10px] text-foreground-muted uppercase tracking-wider block">
              {t('mixer.durationLabel')}
            </span>
            <span className="font-mono font-bold text-foreground">
              {totalDuration.toFixed(1)}s
            </span>
          </div>
          <div>
            <span className="text-[10px] text-foreground-muted uppercase tracking-wider block">
              {t('mixer.tracksLabel')}
            </span>
            <span className="font-mono font-bold text-foreground">
              {tracks.length}
            </span>
          </div>
          <div>
            <span className="text-[10px] text-foreground-muted uppercase tracking-wider block">
              {t('mixer.smartDuckingStatus')}
            </span>
            <span className="font-mono font-bold text-amber-500">
              {duckingSettings.enabled ? `${duckingSettings.duckingPercent}%` : t('mixer.disabledLabel')}
            </span>
          </div>
        </div>

        {/* Format & Sample Rate Options */}
        {!exportedAudioUrl && (
          <div className="space-y-3 text-xs">
            {/* Format Choice */}
            <div>
              <label className="block text-[11px] font-semibold text-foreground-muted mb-1.5">
                {t('mixer.exportFormatLabel')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormat('wav')}
                  className={`p-3 rounded-xl border text-left flex flex-col justify-between transition-all ${
                    format === 'wav'
                      ? 'bg-primary/10 border-primary text-foreground ring-1 ring-primary/30'
                      : 'bg-surface-elevated border-surface-border text-foreground-muted hover:text-foreground'
                  }`}
                >
                  <span className="font-bold text-xs">WAV (16-bit PCM)</span>
                  <span className="text-[10px] text-foreground-muted mt-1">
                    {t('mixer.formatWavDesc')}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setFormat('mp3')}
                  className={`p-3 rounded-xl border text-left flex flex-col justify-between transition-all ${
                    format === 'mp3'
                      ? 'bg-primary/10 border-primary text-foreground ring-1 ring-primary/30'
                      : 'bg-surface-elevated border-surface-border text-foreground-muted hover:text-foreground'
                  }`}
                >
                  <span className="font-bold text-xs">MP3 (320 kbps)</span>
                  <span className="text-[10px] text-foreground-muted mt-1">
                    {t('mixer.formatMp3Desc')}
                  </span>
                </button>
              </div>
            </div>

            {/* Sample Rate */}
            <div>
              <label className="block text-[11px] font-semibold text-foreground-muted mb-1.5">
                {t('mixer.sampleRateLabel')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setSampleRate(44100)}
                  className={`px-3 py-2 rounded-xl border text-center transition-colors ${
                    sampleRate === 44100
                      ? 'bg-surface border-primary text-foreground font-bold'
                      : 'bg-surface-elevated border-surface-border text-foreground-muted hover:text-foreground'
                  }`}
                >
                  44.1 kHz (CD Audio)
                </button>
                <button
                  type="button"
                  onClick={() => setSampleRate(48000)}
                  className={`px-3 py-2 rounded-xl border text-center transition-colors ${
                    sampleRate === 48000
                      ? 'bg-surface border-primary text-foreground font-bold'
                      : 'bg-surface-elevated border-surface-border text-foreground-muted hover:text-foreground'
                  }`}
                >
                  48.0 kHz (Broadcast Video)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar during Rendering */}
        {isExporting && (
          <div className="space-y-2 py-3 text-xs">
            <div className="flex items-center justify-between font-medium">
              <span className="text-foreground flex items-center gap-2">
                <div className="size-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span>{t('mixer.mixingDownTracks')}</span>
              </span>
              <span className="font-mono font-bold text-primary">
                {exportProgress}%
              </span>
            </div>
            <div className="h-2 w-full bg-surface-elevated rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-150"
                style={{ width: `${exportProgress}%` }}
              />
            </div>
            <span className="text-[11px] text-foreground-muted block text-center">
              {t('mixer.clientRenderingNotice')}
            </span>
          </div>
        )}

        {/* Export Completed State */}
        {exportedAudioUrl && (
          <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center space-y-3 animate-slide-up">
            <div className="size-10 rounded-full bg-emerald-500/20 text-emerald-500 flex items-center justify-center mx-auto">
              <CheckCircle2 className="size-6" />
            </div>
            <div>
              <h4 className="text-xs font-bold text-foreground">
                {t('mixer.exportSuccessTitle')}
              </h4>
              <p className="text-[11px] text-foreground-muted mt-0.5">
                {t('mixer.exportSuccessDesc', { format: format.toUpperCase() })}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-2">
              <button
                type="button"
                onClick={handleDownload}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary/90 shadow-xs"
              >
                <Download className="size-3.5" />
                <span>{t('mixer.downloadAudioBtn')}</span>
              </button>

              <button
                type="button"
                disabled={isSavedToGallery}
                onClick={handleSaveToGallery}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-surface-elevated text-foreground text-xs font-semibold hover:bg-surface-border border border-surface-border disabled:opacity-50"
              >
                <HardDrive className="size-3.5 text-primary" />
                <span>
                  {isSavedToGallery
                    ? t('mixer.savedToGallery')
                    : t('mixer.saveToGalleryBtn')}
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="pt-2 border-t border-surface-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={isExporting}
            className="px-4 py-2 text-xs font-medium text-foreground-muted hover:text-foreground rounded-xl"
          >
            {exportedAudioUrl ? t('cat.closeModal') : t('cat.cancelBtn')}
          </button>

          {!exportedAudioUrl && (
            <button
              type="button"
              disabled={isExporting}
              onClick={handleStartRendering}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary/90 shadow-xs disabled:opacity-50"
            >
              <Sparkles className="size-3.5" />
              <span>{t('mixer.startMixdownBtn')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
